import { aiAssistantFixture } from "./fixtures/ai-assistant";

export type AssistantPreview = "available" | "empty" | "error";
export type AssistantClause = {
  id: string;
  title: string;
  article: string;
  original: string;
  proposal: string;
  summary: string;
};
export type AssistantMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
  status: "complete" | "failed";
};
export type AssistantWorkspace = {
  client: string;
  document: string;
  documentStatus: string;
  version: string;
  clauses: AssistantClause[];
  initialMessages: AssistantMessage[];
};

const delay = (ms = 360) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

function copyWorkspace(): AssistantWorkspace {
  return {
    ...aiAssistantFixture,
    clauses: aiAssistantFixture.clauses.map((clause) => ({ ...clause })),
    initialMessages: aiAssistantFixture.initialMessages.map((message) => ({ ...message })),
  };
}

export async function getAssistantWorkspace(preview: AssistantPreview = "available"): Promise<AssistantWorkspace> {
  await delay();
  if (preview === "error") throw new Error("La conversazione demo non è disponibile.");
  const workspace = copyWorkspace();
  if (preview === "empty") workspace.initialMessages = [];
  return workspace;
}

export async function sendAssistantRequest(content: string, options: { retry?: boolean } = {}): Promise<AssistantMessage> {
  await delay(720);
  const text = content.trim();
  if (!text) throw new Error("Scrivi una richiesta prima di inviare.");
  if (text.toLocaleLowerCase("it").includes("errore") && !options.retry) {
    throw new Error("La risposta dimostrativa non è riuscita. Puoi riprovare senza perdere la richiesta.");
  }

  const normalized = text.toLocaleLowerCase("it");
  let contentReply: string;
  if (/(consegna|tempi|giorni)/.test(normalized)) {
    contentReply = "Nel documento dimostrativo, l’articolo 3 non indica un termine preciso. La proposta fissa la consegna entro 15 giorni lavorativi dalla ricezione dell’ordine; il termine resta una proposta da verificare e non modifica il contratto.";
  } else if (/(pagamento|pagare|fattur)/.test(normalized)) {
    contentReply = "La sezione sui pagamenti è presentata come punto da verificare: controlla scadenze, modalità di fatturazione e conseguenze dei ritardi. Questa risposta è dimostrativa e non costituisce un parere legale.";
  } else if (/(durata|rinnovo|recesso)/.test(normalized)) {
    contentReply = "Per durata e rinnovo, verifica la decorrenza, il periodo iniziale e il preavviso richiesto per evitare il rinnovo automatico. Nel mockup puoi aprire la revisione per esaminare il testo di esempio.";
  } else if (/(riepilog|contratt|clausol|rivedere)/.test(normalized)) {
    contentReply = "Nel contratto demo ho individuato tre aree da verificare: durata e rinnovo, tempi di consegna e modalità di pagamento. La proposta sull’articolo 3 indica la consegna entro 15 giorni lavorativi; confrontala con il testo originale prima di decidere.";
  } else {
    contentReply = `Ho ricevuto la richiesta “${text}”. In questa demo posso aiutarti a esplorare durata e rinnovo, tempi di consegna o modalità di pagamento del contratto di fornitura.`;
  }

  return {
    id: `assistant-reply-${Date.now()}`,
    role: "assistant",
    content: contentReply,
    createdAt: "09:12",
    status: "complete",
  };
}
